import { useMemo } from 'react'
import {
  SchedulerDataVariant,
  useAllWeeksSchedulerRows,
} from 'Features/SchedulerView/SchedulerView.data'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'

/**
 * Quantity scheduled per product code, for comparing a week's rows against
 * that week's demand.
 *
 * On a consolidated multi-week run a row that crosses a week boundary is
 * written into EVERY week it touches, at its full quantity, so the Gantt can
 * draw it in each. Summing a week's rows therefore counts it once per week it
 * touches: on 31692634253 week 3's tank draw sat in week 2's rows too and hid
 * week 2's whole shortfall. `demandWeek` (scheduler_api, nullable) says which
 * week's demand a row serves, so a row counts only where that matches the
 * week it is shown in (`weekNumber`). A row without it — a single-week run, a
 * tank fill, or output written before the column existed — counts as before.
 */
/** A numeric cell, or null when it is blank or unreadable — the engine's
 *  `preprocess::get_f64`. A cell left empty is stored as `""` once its whole
 *  column was blank on write, and `parseFloat("")` is NaN while `Number("")`
 *  is 0, so reading it either way invents a value the engine never sees. */
const optionalNumber = (value: unknown): number | null => {
  if (value == null) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const text = String(value).trim()
  if (text === '') return null
  const n = Number(text)
  return Number.isFinite(n) ? n : null
}

/**
 * The quantity a requirement row still asks for, as the engine's
 * `parse_product_requirements` reads it: a stated `quantityStillToMake`
 * overrides `quantity` (0 = already made), otherwise `quantity` less
 * `quantityMade`. On 31689172421 every `quantityStillToMake` was `""`; the
 * header tile read that as NaN and showed "NaN" in green, and the Demand tab
 * read it as 0 and dropped every row as already made, so 2,568 units of unmet
 * demand were reported nowhere.
 */
export const effectiveDemand = (req: {
  quantity?: unknown
  quantityStillToMake?: unknown
  quantityMade?: unknown
}): number => {
  const quantity = optionalNumber(req.quantity) ?? 0
  if (quantity <= 0) return 0
  const stillToMake = optionalNumber(req.quantityStillToMake)
  if (stillToMake !== null) return Math.max(0, stillToMake)
  return Math.max(0, quantity - (optionalNumber(req.quantityMade) ?? 0))
}

export const scheduledByProduct = (rows: InputRow[]): Map<string, number> => {
  const map = new Map<string, number>()
  for (const row of rows) {
    if (!row.productCode) continue
    const demandWeek = row.demandWeek
    if (
      demandWeek != null &&
      row.weekNumber != null &&
      Number(demandWeek) !== Number(row.weekNumber)
    ) {
      continue
    }
    const qty =
      typeof row.quantity === 'string'
        ? parseFloat(row.quantity as unknown as string) || 0
        : (row.quantity ?? 0)
    const key = String(row.productCode)
    map.set(key, (map.get(key) ?? 0) + qty)
  }
  return map
}

/**
 * Quantity scheduled per product code toward `week`'s demand, from the rows of
 * EVERY week. A consolidated run makes a week's demand wherever the plant has
 * room, so counting only the week on screen calls the rest unmet: on
 * 31689172421 week 1's 8,384 units were made across weeks 1-3, of which only
 * 3,447 sat in week 1 — the real shortfall was 310. A row serves
 * `demandWeek`, else the week it sits in; one with neither counts.
 */
export const scheduledForDemandWeek = (
  rows: InputRow[],
  week: number
): Map<string, number> => {
  const map = new Map<string, number>()
  for (const row of rows) {
    if (!row.productCode) continue
    const serves = row.demandWeek ?? row.weekNumber
    if (serves != null && Number(serves) !== week) continue
    const qty = optionalNumber(row.quantity) ?? 0
    const key = String(row.productCode)
    map.set(key, (map.get(key) ?? 0) + qty)
  }
  return map
}

/**
 * What a week's demand view compares against: every week's rows once they
 * have loaded, the week's own rows until then (or when there is no week).
 */
export const useScheduledForDemandWeek = (
  scheduleId: string | undefined,
  iteration: string | undefined,
  week: number | undefined,
  weekRows: InputRow[],
  variant: SchedulerDataVariant = 'original'
): Map<string, number> => {
  const allRows = useAllWeeksSchedulerRows(scheduleId, iteration, variant) as
    | InputRow[]
    | undefined
  return useMemo(
    () =>
      allRows?.length && week != null
        ? scheduledForDemandWeek(allRows, week)
        : scheduledByProduct(weekRows),
    [allRows, week, weekRows]
  )
}
